import React, { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin);

import Scrollspy from 'react-scrollspy';

const Modeling = () => {
  useEffect(() => {
    
  
  }, [])
  
  return (
    <div className="simple_page">
      <div className="mainIntro">
        <h1>Modeling</h1>
        <h3>Cure disease - Secure generations!</h3>
      </div>
      <section className="divider">
        <div className="fixed_sidebar">
          <div className="sidebar">
            <div className="main_sidebar">
              Modeling
            </div>
            <Scrollspy
              offset={-140}
              className="scrollspy" items={['Molecular', 'Experimental', 'References']}
              currentClassName="active">
              <li><a href="#Molecular">Molecular insight into the inhibition of human topoisomerase I by novel tetrahydroxanthone dimers</a></li>
              <li><a href="#Experimental">Experimental section</a></li>
              <li><a href="#References">References</a></li>
            </Scrollspy>
            
          </div>

        </div>
        <div className="main_lab_style">
          <section id="Modeling">
            
            <div>
              <section id="Molecular">
              <p>
                <h1>Molecular insight into the inhibition of human topoisomerase I by novel tetrahydroxanthone dimers</h1>
              </p>
              <p>In the past summer, wet-lab experiments conducted by the CityU iGEM team 2022 have successfully produced two novel tetrahydroxanthone (THX) dimers by constructing an unnatural biosynthetic pathway. As the pathway is based on the one producing secalonic acid D (SAD), a known antitumor agent, the new compounds derived from it are thought to exhibit similar biological activities. Therefore, a comprehensive study on the bioactivity of the new compounds is anticipated. The results from&nbsp; <em>in vitro&nbsp;</em> &nbsp;investigations could be reached in other sections. This section adopted an&nbsp; <em>in silico&nbsp;</em>approach to explore the anticancer property.&nbsp; </p>
              <p>Secalonic acid D is known to exhibit cytotoxicity by inhibiting the topoisomerase I (Topo I) activity (Hong, 2011). Topo I is an essential enzyme regulating cell growth; hence, it is a common target for anticancer therapeutics such as camptothecin and its derivatives. The structural similarities between the novel compounds and SAD provide us a rational prediction that they will also harbor certain Topo I inhibitory activity. Because of this, a molecular docking experiment was carried out, and the inhibitory activity was examined based on the binding affinity between the compound and the human topoisomerase I enzyme. The binding position was subsequently compared with the camptothecin reference to verify the mode of action.&nbsp;</p>
              <p>The two THX dimers isolated in this project were subjected to molecular docking analysis with the human topoisomerase I (PDB ID: 1T8I) utilizing the AutoDock Vina software. Another docking experiment was also performed with SAD using the same set of parameters as a reference. Surprisingly, the binding affinities of&nbsp; <strong>1&nbsp;</strong>and&nbsp; <strong>2&nbsp;</strong>were shown to be higher than that of SAD. The relatively high binding affinity of&nbsp; <strong>1&nbsp;</strong>and&nbsp; <strong>2&nbsp;</strong>indicates that it is likely for them to interact with Topo I. After a careful inspection on the binding modes of all three compounds, it is interesting to find out that the two new compounds have a completely different binding position compared with SAD. It leads us to a hypothesis that&nbsp; <strong>1&nbsp;</strong>and&nbsp; <strong>2&nbsp;</strong>have different Topo I inhibitory mechanisms.&nbsp; </p>
              <p>
                <strong>Table 1.</strong>&nbsp;Binding affinity of the three compounds: SAD, <strong>1</strong> and <strong>2</strong>.
              </p>
              <table>
                <tbody>
                  <tr>
                    <td width={288}>
                      <p>Compounds</p>
                    </td>
                    <td width={288}>
                      <p>Binding affinity (ΔG)</p>
                    </td>
                  </tr>
                  <tr>
                    <td width={288}>
                      <p>SAD</p>
                    </td>
                    <td width={288}>
                      <p>-8</p>
                    </td>
                  </tr>
                  <tr>
                    <td width={288}>
                      <p>
                        <strong>1</strong>
                      </p>
                    </td>
                    <td width={288}>
                      <p>-8.2</p>
                    </td>
                  </tr>
                  <tr>
                    <td width={288}>
                      <p>
                        <strong>2</strong>
                      </p>
                    </td>
                    <td width={288}>
                      <p>-8.2</p>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p>Since a handful number of bioactive natural products elicit toxicity by interacting with Topo I, a detailed search was performed to locate their binding position. Camptothecin (CPT), a previously known antitumor agent, grasps our attention. The co-crystalized structure of camptothecin and Topo I was reported and deposited on the RCSB Protein Data Bank (Staker et al., 2005). The binding positions of&nbsp; <strong>1&nbsp;</strong>and&nbsp; <strong>2&nbsp;</strong>are found to be located in the same cavity as CPT. Therefore, we propose the Topo I inhibitory activity of&nbsp; <strong>1&nbsp;</strong>and&nbsp; <strong>2&nbsp;</strong>would be more similar to that of CPT than SAD.&nbsp; </p>
              <p>
                <strong>Figure 1.</strong>&nbsp;The binding pocket of CPT showing the similarity between the binding of CPT (red), <strong>1 </strong>(blue), and <strong>2 </strong>(yellow). The binding site of SAD (pink) is at the different side of the enzyme.&nbsp;
                  <img src="https://static.igem.wiki/teams/4154/wiki/assets/picture3.png" alt="" />
              </p>
              <p>To further explore the mechanism of inhibition, the interacting residues near <strong>1 </strong>and <strong>2</strong> were visualized with LigPlot+. Some critical interacting residues in the CPT’s binding, such as Arg-364, Lys-532, and Asp-533, can also be observed in our case (Figure 2). It, again, shows that&nbsp; <strong>1</strong>&nbsp;&amp;&nbsp; <strong>2</strong>&nbsp;may have the ability to inhibit Topo I in a way similar to CPT.&nbsp; </p>
              <p>In the&nbsp; <em>in vitro&nbsp;</em>experiments,&nbsp; <strong>1&nbsp;</strong>is shown to be more active than&nbsp; <strong>2</strong>. Intriguingly, the&nbsp; <em>in silico&nbsp;</em>data we obtained herein can indeed provide a possible explanation to it. It is previously known that the residue Lys-532 carries a crucial role in Topo I’s catalytic action (Krogh &amp; Shuman, 2000). The blockage or mutation on this residue often leads to a complete abolishment of the activity. From the result,&nbsp; <strong>1</strong>&nbsp;can form a strong hydrogen bond with Lys-532. On the other hand,&nbsp; <strong>2</strong>&nbsp;can only form weak hydrophobic interactions with it. The decrease in activity may be due to a looser linkage between the molecule itself and the catalytic residue.&nbsp; </p>
              <p>All in all, the Topo I inhibitory action of the two new THX dimers isolated by our team was demonstrated with an&nbsp; <em>in silico&nbsp;</em>approach. It not only helps us predict the new compounds' bioactivity but also provides a rational explanation to previous&nbsp; <em>in vitro&nbsp;</em>data. It is hoped that this work can provide a solid foundation for future mechanistic studies on the anticancer properties of&nbsp; <strong>1&nbsp;</strong>and&nbsp; <strong>2</strong>.&nbsp; </p>
              <p>
                <strong>Figure 2.</strong>&nbsp;LigPlot+ diagram of <strong>1</strong> and <strong>2</strong>.&nbsp;
                  <img src="https://static.igem.wiki/teams/4154/wiki/assets/picture2.png" alt="" />
              </p>
              <p>&nbsp;</p>
              </section>
              <section id="Experimental">
              <p>
                <h1>Experimental section </h1>
              </p>
              <p>
                <strong>Preparation of ligands.</strong> The structural information of <strong>1</strong> and <strong>2</strong> was prepared with ChemDraw according to NMR data obtained in web-lab experiments while the structure of SAD was retrieved from the PubChem website (https://pubchem.ncbi. nlm.nih.gov/). 3D structures were exported from Chem3D and refined using Avogadro (Hanwell et al., 2012). All structures were optimized with the MMFF94s force field (Halgren, 1999).
              </p>
              <p>
                <strong>Preparation of receptor</strong>. The 3D crystal structure of the human topoisomerase I was retrieved from the Protein Data Bank (PDB) using PDB ID: 1T8I.
              </p>
              <p>
                <strong>Molecular docking and binding affinity evaluation.</strong> The AutoDock Vina software was used to perform docking analysis (Trott &amp; Olson, 2010). The most favorable binding affinity was chosen based on the lowest free energy of binding (ΔG). The binding affinity (ΔG°) as a function of the binding constant (Kb) was expressed using the formula:
              </p>
              <p>ΔG° = −RTlnKb</p>
              <p>ΔG° represents the binding affinity of a ligand with the human Topoisomerase I.</p>
              <p>
                <strong>Interacting residues visualization.</strong> The LigPlot+ software was used to visualize the interacting amino acid residues of the compound (Laskowski &amp; Swindells, 2011).
              </p>
              </section>
              <section id="References">

              
              <h1>References:</h1>
              <ol>
                  <li>Halgren, T. A. (1999). MMFF VI. MMFF94s option for energy minimization studies.&nbsp; <em>Journal of computational chemistry</em>,&nbsp; <em>20</em>(7), 720-729. </li>
                  <li>Hanwell, M. D., Curtis, D. E., Lonie, D. C., Vandermeersch, T., Zurek, E., &amp; Hutchison, G. R. (2012). Avogadro: an advanced semantic chemical editor, visualization, and analysis platform.&nbsp; <em>Journal of cheminformatics</em>,&nbsp; <em>4</em>(1), 1-17. </li>
                  <li>Hong, R. (2011). Secalonic acid D as a novel DNA topoisomerase I inhibitor from marine lichen-derived fungus Gliocladium sp. T31.&nbsp; <em>Pharmaceutical Biology</em>,&nbsp; <em>49</em>(8), 796-799. </li>
                  <li>Krogh, B. O., &amp; Shuman, S. (2000). Catalytic mechanism of DNA topoisomerase IB.&nbsp; <em>Molecular cell</em>,&nbsp; <em>5</em>(6), 1035-1041. </li>
                  <li>Laskowski, R. A., &amp; Swindells, M. B. (2011). LigPlot+: multiple ligand–protein interaction diagrams for drug discovery.</li>
                  <li>Staker, B. L., Feese, M. D., Cushman, M., Pommier, Y., Zembower, D., Stewart, L., &amp; Burgin, A. B. (2005). Structures of three classes of anticancer agents bound to the human topoisomerase I− DNA covalent complex.&nbsp; <em>Journal of medicinal chemistry</em>,&nbsp; <em>48</em>(7), 2336-2345. </li>
                  <li>Trott, O., &amp; Olson, A. J. (2010). AutoDock Vina: improving the speed and accuracy of docking with a new scoring function, efficient optimization, and multithreading.&nbsp; <em>Journal of computational chemistry</em>,&nbsp; <em>31</em>(2), 455-461. </li>

              </ol>
              </section>
            </div>

          </section>
        </div>
      </section>
    </div>
  );
}

export default Modeling;
